import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Mail, Copy, Check, Download, FileText, MessageSquare,
  Users, DollarSign, AlertTriangle, Calendar, CheckCircle,
  ClipboardList, Send, RefreshCw, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

// Email templates for common situations
const EMAIL_TEMPLATES = {
  welcome_guide: {
    subject: "Bienvenue sur XPerience ! 🎉",
    body: `Bonjour [NOM],

Bienvenue dans la communauté XPerience !

Nous sommes ravis de vous compter parmi nos guides locaux. Voici les prochaines étapes :

1. Complétez votre profil (photo, bio, langues parlées)
2. Créez votre première expérience ou meetup
3. Partagez votre profil sur vos réseaux sociaux

Quelques conseils pour réussir :
- Des photos de qualité font toute la différence
- Soyez précis dans vos descriptions
- Répondez rapidement aux demandes

Si vous avez des questions, consultez notre FAQ : [LIEN_FAQ]

À très bientôt !
L'équipe XPerience`
  },
  booking_reminder: {
    subject: "Rappel : Votre expérience demain",
    body: `Bonjour [NOM],

Ceci est un rappel pour votre expérience prévue demain :

📍 Expérience : [TITRE_EXPERIENCE]
📅 Date : [DATE]
👤 Guide : [NOM_GUIDE]
📍 Lieu de rendez-vous : [LIEU]

N'oubliez pas :
- Arrivez 10 minutes en avance
- Vérifiez la météo et adaptez votre tenue
- Apportez de l'eau et de bonnes chaussures

Les coordonnées de votre guide sont disponibles dans votre espace réservation.

Bonne expérience !
L'équipe XPerience`
  },
  review_request: {
    subject: "Comment s'est passée votre expérience ?",
    body: `Bonjour [NOM],

Nous espérons que votre expérience "[TITRE_EXPERIENCE]" avec [NOM_GUIDE] s'est bien passée !

Votre avis compte beaucoup :
- Pour aider les futurs voyageurs à choisir
- Pour récompenser les guides qui font du bon travail
- Pour nous améliorer

👉 Laissez votre avis ici : [LIEN_AVIS]

Merci de faire partie de la communauté XPerience !

À bientôt pour de nouvelles aventures,
L'équipe XPerience`
  },
  guide_rejection: {
    subject: "Votre demande de vérification XPerience",
    body: `Bonjour [NOM],

Merci de votre intérêt pour devenir guide sur XPerience.

Après examen de votre profil, nous ne pouvons pas valider votre demande pour le moment.

Raison : [RAISON]

Ce que vous pouvez faire :
- Compléter les informations manquantes
- Améliorer la qualité de vos photos
- Détailler davantage votre bio et vos expériences

Une fois les modifications effectuées, vous pouvez soumettre une nouvelle demande.

Si vous avez des questions, n'hésitez pas à nous contacter.

Cordialement,
L'équipe XPerience`
  },
  dispute_response: {
    subject: "Concernant votre signalement",
    body: `Bonjour [NOM],

Nous avons bien reçu votre signalement concernant [OBJET].

Notre équipe examine actuellement la situation. Nous reviendrons vers vous sous 48h avec une réponse.

En attendant, merci de ne pas contacter directement l'autre partie.

Référence du dossier : [REF]

Cordialement,
L'équipe XPerience`
  },
  refund_confirmation: {
    subject: "Confirmation de remboursement",
    body: `Bonjour [NOM],

Suite à l'annulation de votre réservation, nous confirmons le remboursement suivant :

📍 Expérience : [TITRE_EXPERIENCE]
💰 Montant remboursé : [MONTANT]€
📅 Délai : 5-10 jours ouvrés

Le remboursement sera crédité sur le moyen de paiement utilisé lors de la réservation.

Si vous avez des questions, n'hésitez pas à nous contacter.

Cordialement,
L'équipe XPerience`
  },
  inactive_guide: {
    subject: "Votre compte XPerience vous attend !",
    body: `Bonjour [NOM],

Nous avons remarqué que vous n'avez pas été actif sur XPerience depuis un moment.

Savez-vous que :
- De nombreux voyageurs recherchent des expériences dans votre région
- Vous pouvez proposer des meetups gratuits pour commencer
- Votre profil est visible mais sans expériences actives

Besoin d'aide pour créer votre première expérience ? Répondez à cet email !

À bientôt sur XPerience,
L'équipe XPerience`
  }
};

// Pre-written support responses
const SUPPORT_RESPONSES = {
  how_to_book: {
    title: "Comment réserver ?",
    response: `Pour réserver une expérience :
1. Choisissez une expérience qui vous plaît
2. Sélectionnez une date disponible
3. Indiquez le nombre de participants
4. Procédez au paiement sécurisé

Vous recevrez une confirmation par email avec tous les détails.`
  },
  cancellation: {
    title: "Politique d'annulation",
    response: `Notre politique d'annulation :
• Plus de 24h avant : remboursement intégral (100%)
• Moins de 24h avant : aucun remboursement (0%)

Pour annuler, rendez-vous dans "Mes réservations" sur votre tableau de bord.`
  },
  become_guide: {
    title: "Devenir guide",
    response: `Pour devenir guide sur XPerience :
1. Créez un compte en tant qu'"Explorateur"
2. Complétez votre profil (photo, bio, langues)
3. Créez votre première expérience ou meetup
4. Attendez la validation de notre équipe

L'inscription est gratuite. Nous prélevons 15% de commission sur les expériences payantes.`
  },
  payment_issue: {
    title: "Problème de paiement",
    response: `Si vous rencontrez un problème de paiement :
1. Vérifiez que votre carte est valide et non expirée
2. Vérifiez que vous avez des fonds suffisants
3. Essayez un autre moyen de paiement

Si le problème persiste, contactez votre banque ou essayez depuis un autre navigateur.`
  },
  contact_guide: {
    title: "Contacter un guide",
    response: `Pour contacter un guide :
• Avant réservation : utilisez la messagerie intégrée
• Après réservation confirmée : les coordonnées du guide sont disponibles dans votre réservation

Note : Pour votre sécurité, nous masquons les coordonnées tant que la réservation n'est pas confirmée.`
  },
  report_issue: {
    title: "Signaler un problème",
    response: `Pour signaler un problème :
1. Rendez-vous sur la réservation concernée
2. Cliquez sur "Signaler un problème"
3. Décrivez la situation en détail

Notre équipe examinera votre signalement sous 48h. En cas d'urgence, répondez directement à cet email.`
  }
};

// Copy to clipboard helper
const copyToClipboard = async (text, successMessage) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage || 'Copié !');
  } catch (err) {
    toast.error('Erreur lors de la copie');
  }
};

// Email Template Card
const EmailTemplateCard = ({ template, title, icon: Icon }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const fullEmail = `Objet : ${template.subject}\n\n${template.body}`;
    await copyToClipboard(fullEmail, 'Email copié !');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border rounded-xl p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-primary" />
          </div>
          <h3 className="font-semibold text-sm">{title}</h3>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCopy}
          className="h-8"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-2">Objet : {template.subject}</p>
      <p className="text-xs text-gray-600 line-clamp-3">{template.body.substring(0, 150)}...</p>
    </motion.div>
  );
};

// Support Response Card
const SupportResponseCard = ({ response, title }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyToClipboard(response, 'Réponse copiée !');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-sm">{title}</span>
        <Button size="sm" variant="ghost" onClick={handleCopy} className="h-7 px-2">
          {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
        </Button>
      </div>
      <p className="text-xs text-gray-600 whitespace-pre-line line-clamp-3">{response}</p>
    </div>
  );
};

// Main Admin Tools Component
const AdminToolsPanel = () => {
  const { i18n } = useTranslation();
  const [exporting, setExporting] = useState(false);

  // Helper to get API URL - uses current origin in production
  const getApiUrl = () => {
    if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
      return window.location.origin;
    }
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  };
  const API_URL = getApiUrl();

  // Export functions
  const exportData = async (type) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/admin/export/${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `xperience_${type}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Export réussi !');
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="emails" className="w-full">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="emails" className="text-xs">
            <Mail className="w-3 h-3 mr-1" />
            Emails
          </TabsTrigger>
          <TabsTrigger value="support" className="text-xs">
            <MessageSquare className="w-3 h-3 mr-1" />
            Support
          </TabsTrigger>
          <TabsTrigger value="exports" className="text-xs">
            <Download className="w-3 h-3 mr-1" />
            Exports
          </TabsTrigger>
        </TabsList>

        {/* Email Templates Tab */}
        <TabsContent value="emails" className="mt-4">
          <div className="mb-4">
            <h3 className="font-semibold text-lg mb-1">Templates d'emails</h3>
            <p className="text-sm text-gray-500">Cliquez pour copier et personnalisez les [CHAMPS]</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <EmailTemplateCard
              template={EMAIL_TEMPLATES.welcome_guide}
              title="Bienvenue guide"
              icon={Users}
            />
            <EmailTemplateCard
              template={EMAIL_TEMPLATES.booking_reminder}
              title="Rappel réservation"
              icon={Calendar}
            />
            <EmailTemplateCard
              template={EMAIL_TEMPLATES.review_request}
              title="Demande d'avis"
              icon={CheckCircle}
            />
            <EmailTemplateCard
              template={EMAIL_TEMPLATES.guide_rejection}
              title="Refus vérification"
              icon={AlertTriangle}
            />
            <EmailTemplateCard
              template={EMAIL_TEMPLATES.dispute_response}
              title="Réponse litige"
              icon={AlertTriangle}
            />
            <EmailTemplateCard
              template={EMAIL_TEMPLATES.refund_confirmation}
              title="Confirmation remboursement"
              icon={DollarSign}
            />
            <EmailTemplateCard
              template={EMAIL_TEMPLATES.inactive_guide}
              title="Relance guide inactif"
              icon={RefreshCw}
            />
          </div>
        </TabsContent>

        {/* Support Responses Tab */}
        <TabsContent value="support" className="mt-4">
          <div className="mb-4">
            <h3 className="font-semibold text-lg mb-1">Réponses pré-écrites</h3>
            <p className="text-sm text-gray-500">Réponses aux questions fréquentes - Cliquez pour copier</p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {Object.entries(SUPPORT_RESPONSES).map(([key, data]) => (
              <SupportResponseCard key={key} title={data.title} response={data.response} />
            ))}
          </div>
        </TabsContent>

        {/* Exports Tab */}
        <TabsContent value="exports" className="mt-4">
          <div className="mb-4">
            <h3 className="font-semibold text-lg mb-1">Exports de données</h3>
            <p className="text-sm text-gray-500">Exportez vos données au format CSV pour votre comptable</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Transactions</h4>
                  <p className="text-xs text-gray-500">Paiements et commissions</p>
                </div>
              </div>
              <Button
                onClick={() => exportData('transactions')}
                disabled={exporting}
                className="w-full"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Exporter CSV
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white border rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Guides</h4>
                  <p className="text-xs text-gray-500">Liste des guides inscrits</p>
                </div>
              </div>
              <Button
                onClick={() => exportData('guides')}
                disabled={exporting}
                variant="outline"
                className="w-full"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Exporter CSV
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white border rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Réservations</h4>
                  <p className="text-xs text-gray-500">Historique des réservations</p>
                </div>
              </div>
              <Button
                onClick={() => exportData('bookings')}
                disabled={exporting}
                variant="outline"
                className="w-full"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Exporter CSV
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white border rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Expériences</h4>
                  <p className="text-xs text-gray-500">Catalogue des expériences</p>
                </div>
              </div>
              <Button
                onClick={() => exportData('experiences')}
                disabled={exporting}
                variant="outline"
                className="w-full"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Exporter CSV
              </Button>
            </motion.div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminToolsPanel;
export { EMAIL_TEMPLATES, SUPPORT_RESPONSES };
